import { useEffect, useMemo, useState } from 'react';

import type { LunaClient } from '@vlabs/api-bindings/src';
import { apiContainer } from '@vlabs/api-bindings/src';
import { useLocation, useParams } from 'react-router-dom';

import type { TImageCheckFormSubmitValues } from './image-check-form/types';
import type { TDataState } from './types';
import { handleResultData } from './utils';

const TASK_STATUS = {
  0: 'pending',
  1: 'in_progress',
  2: 'success', // 'done'
  3: 'error', // 'failed'
};

export interface UseTaskDataReturn {
  data?: TImageCheckFormSubmitValues;
  state: TDataState;
}

export function useTaskData(): UseTaskDataReturn {
  const [data, setData] = useState<TImageCheckFormSubmitValues | undefined>(undefined);
  const [state, setState] = useState<TDataState>('notAsked');
  const [taskProgress, setTaskProgress] = useState({ progress: 0, status: 'pending' });

  const { taskId } = useParams<{
    taskId: string;
  }>();
  const { search } = useLocation();

  const lunaClient = (apiContainer.lunaClient as LunaClient);

  useEffect(() => {
    if (!taskId) {
      setState('notAsked');
      setTaskProgress(null);
      setData(null);
      return;
    }

    let isActive = true;
    let timeoutId = null;

    const scheduleNextPoll = () => {
      if (!isActive) return;
      timeoutId = setTimeout(pollForResultUrl, 5000);
    };

    const pollForResultUrl = async () => {
      if (!isActive) return;

      try {
        const resp = await lunaClient.videoTasks.get(taskId);
        if (!isActive) return;

        const { progress, status, result_url: resultUrl } = resp?.data ?? {};

        setTaskProgress({
          progress: Math.round(Number(progress) * 100),
          status: TASK_STATUS[status],
        });

        if (!resultUrl) {
          setState('loading');
          scheduleNextPoll();
          return;
        }
        const objectId = resultUrl.split('/').pop() ?? '';
        const resultJson = await lunaClient.objects.get(objectId, {
          params: {
            with_meta: 1,
          },
        });
        if (!isActive) return;
        if (resultJson.data?.response?.error_code) {
          setData(null);
          setState('failed');
          isActive = false;
          return;
        }

        const resultData = await handleResultData(resultJson.data);
        if (!isActive) return;

        setData(resultData);
      } catch (e) {
        if (!isActive) return;
        setData(null);
        setState('failed');
        throw e;
      }
    };

    pollForResultUrl();

    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
      setState('notAsked');
      setTaskProgress(null);
      setData(null);
    };
  }, [taskId, search]);

  return useMemo(() => ({
    data,
    state,
    taskProgress,
  }), [data, state, taskProgress]);
}
